import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useNotifications } from '../../components/common/NotificationSystem';
import { authService } from '../../services/authService';
import ProfessionalLoader from "../../components/common/ProfessionalLoader";
import Logo from '../../components/common/Logo';
import './Auth.css';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useNotifications();

  const uid = searchParams.get('uid');
  const token = searchParams.get('token');

  // Validate token and get user info on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!uid || !token) {
        setErrors({ general: 'Invalid reset link. Missing required parameters.' });
        setIsValidating(false);
        return;
      }

      try {
        const response = await authService.validatePasswordResetToken(uid, token);

        if (response.success && response.data.valid) {
          setUserInfo(response.data.user);
          setIsValidating(false);
        } else {
          setErrors({ general: response.data?.error || 'Invalid or expired reset link. Please request a new one.' });
          setIsValidating(false);
        }
      } catch (error) {
        setErrors({ general: 'Failed to validate reset link. Please try again.' });
        setIsValidating(false);
      }
    };

    validateToken();
  }, [uid, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!uid || !token) {
      setErrors({ general: 'Invalid reset link. Please request a new password reset.' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Use authService for password reset
      const response = await authService.resetPassword(uid, token, formData.password, formData.confirmPassword);

      if (response.success) {
        setIsSuccess(true);
        showSuccess('Password reset successfully', 'Success');
      } else {
        // Handle backend-specific error responses
        let errorMessage = 'Password reset failed. Please try again.';

        if (response.data) {
          // Handle field-specific errors from backend
          if (response.data.new_password) {
            errorMessage = response.data.new_password[0] || errorMessage;
          } else if (response.data.password_confirm) {
            errorMessage = response.data.password_confirm[0] || errorMessage;
          } else if (response.data.error) {
            errorMessage = response.data.error;
          }
        } else if (response.message) {
          errorMessage = response.message;
        }

        setErrors({ general: errorMessage });
        showError(errorMessage, 'Password Reset Failed');
      }

    } catch (error) {
      let errorMessage = 'Password reset failed. Please try again.';

      // Handle specific error cases
      if (error.message) {
        if (error.message.includes('token') && error.message.includes('invalid')) {
          errorMessage = 'This reset link has expired or is invalid. Please request a new password reset.';
        } else if (error.message.includes('password') && error.message.includes('validation')) {
          errorMessage = 'Password does not meet the requirements. Please check the password criteria below.';
        } else {
          errorMessage = error.message;
        }
      }

      setErrors({ general: errorMessage });
      showError(errorMessage, 'Password Reset Failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (isValidating) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <ProfessionalLoader size="large" variant="default" />
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            </div>
            <h1>Password Reset Successful</h1>
            <p>Your password has been successfully updated</p>
          </div>

          <div className="success-message">
            <p>You can now sign in with your new password.</p>
          </div>

          <div className="auth-footer">
            <Link to="/login" className="auth-button">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!uid || !token) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Invalid Reset Link</h1>
            <p>The password reset link is invalid or has expired</p>
          </div>

          <div className="auth-footer">
            <Link to="/forgot-password" className="auth-button">
              Request New Reset Link
            </Link>
            <p><Link to="/login">Back to Sign In</Link></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Logo
            className="auth-logo"
            alt="AFF Logo"
          />
          <h1>Set New Password</h1>
          {userInfo && (
            <div className="user-info">
              <p>Resetting password for: <strong>{userInfo.email}</strong></p>
              {userInfo.first_name && userInfo.last_name && (
                <p>Welcome back, {userInfo.first_name} {userInfo.last_name}!</p>
              )}
            </div>
          )}
          <p>Enter your new password below. Make sure it meets all the security requirements.</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {errors.general && (
            <div className="error-message">{errors.general}</div>
          )}

          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            {errors.password && <span className="field-error">{errors.password}</span>}

            {/* Password Requirements */}
            <div className="password-requirements">
              <h4>Password Requirements:</h4>
              <ul>
                <li className={formData.password.length >= 8 ? 'valid' : ''}>
                  At least 8 characters long
                </li>
                <li className={/(?=.*[a-z])/.test(formData.password) ? 'valid' : ''}>
                  Contains at least one lowercase letter
                </li>
                <li className={/(?=.*[A-Z])/.test(formData.password) ? 'valid' : ''}>
                  Contains at least one uppercase letter
                </li>
                <li className={/(?=.*\d)/.test(formData.password) ? 'valid' : ''}>
                  Contains at least one number
                </li>
              </ul>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
            {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p><Link to="/login">Back to Sign In</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
